import { faAngleDown } from '@fortawesome/pro-regular-svg-icons/faAngleDown';
import { faArrowRightFromBracket } from '@fortawesome/pro-solid-svg-icons/faArrowRightFromBracket';
import { faGlobe } from '@fortawesome/pro-solid-svg-icons/faGlobe';
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser';
import { faUserCheck } from '@fortawesome/pro-solid-svg-icons/faUserCheck';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import clsx from 'clsx';
import { type FC, useState } from 'react';
import { Container, Nav, Navbar } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { AuthState, useAuth, useIsRegistered } from '@furality/frontend-stack';
import { Button, Menu } from '@furality/furality-purpose';
import FontAwesomeIcon from '#/components/FontAwesomeIcon.tsx';
import { useInstancePreferences } from '@/components/__app/InstancePreferenceContext.tsx';
import LanguageChangerDropdown from '@/components/LanguageChangerDropdown.tsx';
import Placeholder from '@/components/Placeholder.tsx';
import useFlag from '@/lib/hooks/useFlag.ts';
import useLoading from '@/lib/hooks/useLoading.ts';

import style from './Header.module.scss';
import useDefaultPages from './useDefaultPages.ts';

type HeaderProps = {
  gradient?: boolean;
  hideLogo?: boolean;
};
const Header: FC<HeaderProps> = ({ gradient = false, hideLogo = false }) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const loading = useLoading();
  const links = useDefaultPages();
  const { registered } = useIsRegistered();
  const enableExplore = useFlag('explore');
  const enableStatusPage = useFlag('status');
  const enableSquads = useFlag('squads');
  const instancePrefs = useInstancePreferences();
  const { t } = useTranslation(['common', 'explore']);

  const [expanded, setExpanded] = useState(false);

  return (
    <header
      className={clsx(style['header'], 'header z-1', expanded && 'header-collapse-show')}
      style={{ position: 'unset', height: 'fit-content' }}
    >
      <Navbar
        bg="dark"
        variant="dark"
        expand="lg"
        className={clsx(
          'furality-navbar',
          expanded && 'navbar-collapsed',
          gradient && 'navbar-gradient',
        )}
        expanded={expanded}
        onToggle={setExpanded}
      >
        <Container className="px-lg-0">
          <Navbar.Brand
            style={{ opacity: hideLogo ? '0' : '1' }}
            as={Link}
            to={enableExplore ? '/explore' : '/profile'}
          >
            <img
              alt="Furality event logo"
              // CON-SPECIFIC: event logo
              src="https://cdn.furality.org/files/web/logo/furality-flower-festival-logo-md.webp"
              height={70}
            />
          </Navbar.Brand>
          <Navbar.Toggle
            className="navbar-toggler"
            aria-controls="navbar-main-collapse"
            aria-expanded={expanded}
          />
          <Navbar.Collapse className={clsx(expanded || 'collapsing-out')}>
            {registered && (
              <Nav as="ul">
                {links.map((link) =>
                  enableSquads && link.subpages !== undefined && link.subpages.length > 0 ? (
                    <Menu title={t(link.label)} key={t(link.label)} align="end">
                      {link.subpages.map((subpage) => (
                        <li key={subpage.label}>
                          <Link to={subpage.href} className="d-flex align-items-center gap-3">
                            {t(subpage.label)}
                          </Link>
                        </li>
                      ))}
                    </Menu>
                  ) : (
                    <Nav.Item as="li" key={link.href}>
                      <Nav.Link
                        active={pathname === link.href}
                        onClick={() => setExpanded(false)}
                        as={Link}
                        to={link.href}
                      >
                        {loading ? <Placeholder width="4em" /> : t(link.label)}
                      </Nav.Link>
                    </Nav.Item>
                  ),
                )}
              </Nav>
            )}

            <Nav as="ul" className="ml-lg-auto">
              <LanguageChangerDropdown />

              {auth.state === 'loggedIn' && (
                <Menu
                  title={
                    <>
                      <FontAwesomeIcon icon={faUser} className="mx-0" />
                      <span className="visually-hidden">{auth.user.name}</span>
                    </>
                  }
                  align="start"
                >
                  {enableStatusPage && (
                    <li>
                      <Link to="/status" className="d-flex align-items-center gap-3">
                        <FontAwesomeIcon icon={faUserCheck} />
                        {t('shared.navigation.status')}
                      </Link>
                    </li>
                  )}
                  {registered && (
                    <li>
                      <button
                        type="button"
                        className="unstyled-button d-flex align-items-center gap-3"
                        onClick={() => instancePrefs.show()}
                      >
                        <FontAwesomeIcon icon={faGlobe} />
                        {t('navigation.set_instance_preferences')}
                      </button>
                    </li>
                  )}
                  <li>
                    <button
                      type="button"
                      className="unstyled-button d-flex align-items-center gap-3"
                      onClick={() => {
                        auth.logout();
                        void navigate({ to: '/' });
                      }}
                    >
                      <FontAwesomeIcon icon={faArrowRightFromBracket} />
                      <span>{t('logout')}</span>
                    </button>
                  </li>
                </Menu>
              )}

              {auth.state === AuthState.Loading && (
                <Nav.Item as="li" className="d-flex align-items-center">
                  <span style={{ minWidth: '100px' }} className="d-block text-nowrap">
                    <Placeholder className="w-100" />{' '}
                    <FontAwesomeIcon icon={faAngleDown} className="d-inline" />
                  </span>
                </Nav.Item>
              )}

              {auth.state !== AuthState.Loading && auth.state !== AuthState.LoggedIn && (
                <Nav.Item as="li" className="d-flex align-items-center">
                  <Button
                    variant="primary"
                    size="sm"
                    className="d-lg-inline-flex ms-xl-3 ms-1"
                    onClick={() => auth.login()}
                  >
                    {t('shared.login_button')}
                  </Button>
                </Nav.Item>
              )}

              {!registered && (
                <Nav.Item as="li" className="d-flex align-items-center">
                  <Button
                    as="a"
                    href="/register"
                    size="sm"
                    variant="warning text-dark text-decoration-none"
                    className="d-lg-inline-flex ms-xl-3 ms-1"
                  >
                    {t('shared.register.button')}
                  </Button>
                </Nav.Item>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>
    </header>
  );
};
export default Header;
